import {
  AcademyClass,
  AcademyClassStatus,
  AcademyClassV2Status,
  CourseClass,
  Match,
  PublicClass,
  Sport,
  Tournament,
} from '@syltek/nemo-api-types'
import { clsx, type ClassValue } from 'clsx'
import { DateTime } from 'luxon'
import { twMerge } from 'tailwind-merge'
import { Activity, ActivityType } from './types'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const getSportColor = (sport: Sport) => {
  switch (sport) {
    case 'TENNIS':
      return 'bg-green-100 text-green-800'
    case 'PADEL':
      return 'bg-blue-100 text-blue-800'
    case 'PICKLEBALL':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getActivityTypeBackgroundColor = (type: ActivityType) => {
  switch (type) {
    case 'PRIVATE':
      return 'bg-purple-400'
    case 'COURSE':
      return 'bg-orange-400'
    case 'PUBLIC':
      return 'bg-indigo-400'
    case 'QUICK':
    case 'OPEN_PLAY':
    case 'CLINIC':
      return 'bg-amber-400'
    case 'MATCH':
      return 'bg-emerald-500'
    default:
      return 'bg-gray-400'
  }
}

const getActivityTypeBorderColor = (type: ActivityType) => {
  switch (type) {
    case 'PRIVATE':
      return 'border-l-purple-400'
    case 'COURSE':
      return 'border-l-orange-400'
    case 'PUBLIC':
      return 'border-l-indigo-400'
    case 'QUICK':
    case 'OPEN_PLAY':
    case 'CLINIC':
      return 'border-l-amber-400'
    case 'MATCH':
      return 'border-l-emerald-500'
    default:
      return 'border-l-gray-400'
  }
}

const getActivityTypeColor = (type: ActivityType) => {
  switch (type) {
    case 'PRIVATE':
      return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'COURSE':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'PUBLIC':
      return 'bg-cyan-100 text-cyan-800 border-cyan-200'
    case 'QUICK':
    case 'OPEN_PLAY':
    case 'CLINIC':
      return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'MATCH':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const getInitials = (name: string | undefined) => {
  if (!name) return 'PL'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * Get the border color based on activity status.
 * Works with both AcademyClassStatus and AcademyClassV2Status
 */
function getStatusColor(status: AcademyClassStatus | AcademyClassV2Status) {
  switch (status) {
    case 'PENDING':
      return 'border-l-yellow-500'
    case 'IN_PROGRESS':
      return 'border-l-energy-blue'
    case 'FINISHED':
    case 'CANCELED':
      return 'border-l-gray-400'
    default:
      return 'border-l-gray-300'
  }
}

/**
 * Get the duration of an activity in minutes based on the start and end date
 *
 * If the start or end date is not provided, or if there's any problem during the conversion, return 0
 */
const getActivityDuration = (startDate: string | null, endDate: string | null) => {
  if (!startDate || !endDate) return 0

  const start = DateTime.fromISO(startDate)
  const end = DateTime.fromISO(endDate)

  return end.diff(start, 'minutes').toObject().minutes ?? 0
}

function isCourseClass(activity: AcademyClass): activity is CourseClass {
  return activity.type === 'COURSE' && 'course_summary' in activity
}

/**
 * Type guard to check if an activity is a Match
 * Matches have a match_id and teams array but no 'type' property
 * Check this first before other type guards
 */
function isMatch(activity: Activity): activity is Match {
  return 'match_id' in activity && 'teams' in activity
}

/**
 * Type guard to check if an activity is a PublicClass
 */
function isPublicClass(activity: Activity): activity is PublicClass {
  if (isMatch(activity)) return false
  return activity.type === 'PUBLIC' && 'name' in activity && 'images' in activity
}

/**
 * Type guard to check if an activity is a Tournament (QUICK, OPEN_PLAY or CLINIC)
 */
function isTournament(activity: Activity): activity is Tournament {
  if (isMatch(activity)) return false
  return activity.type === 'QUICK' || activity.type === 'OPEN_PLAY' || activity.type === 'CLINIC'
}

/**
 * Type guard to check if an activity is an AcademyClass v1 (PRIVATE or COURSE with singular resource)
 * This distinguishes from AcademyClassV2 which uses resources[] array
 */
function isAcademyClass(activity: Activity): activity is AcademyClass {
  if (isMatch(activity)) return false
  return (activity.type === 'PRIVATE' || activity.type === 'COURSE') && 'resource' in activity
}

// TODO: find a better way to do this
function getTenantColor(index: number) {
  const colors = [
    '#2C54E8', // midas blue-60 (highlight.emphasis)
    '#38C679', // midas green-60 (success.emphasis)
    '#F6AC1E', // midas yellow-60 (warning.emphasis)
    '#FD3F58', // midas red-50
    '#404459', // midas navy-60
  ]

  return colors[index % colors.length]
}

export {
  cn,
  getActivityDuration,
  getActivityTypeBackgroundColor,
  getActivityTypeBorderColor,
  getActivityTypeColor,
  getInitials,
  getSportColor,
  getStatusColor,
  getTenantColor,
  isAcademyClass,
  isCourseClass,
  isMatch,
  isPublicClass,
  isTournament,
}
